import React, { useState, useRef, useEffect } from "react";
import "./InputBox.css";
import { useTranslation } from "react-i18next";

export const RESTRICTIONS = {
  "common-safe": {
    pattern: /^[a-zA-Z0-9-]*$/,
    maxLength: 15,
    minLength: 8,
    error: "Must be 8-15 characters (letters, numbers, or hyphens)",
  },
  numeric: {
    pattern: /^[0-9]*$/,
    error: "Only numbers are allowed",
  },
  alpha: {
    pattern: /^[a-zA-Z]*$/,
    error: "Only letters are allowed",
  },
  "alpha-numeric": {
    pattern: /^[a-zA-Z0-9]*$/,
    error: "Only letters and numbers are allowed",
  },
};

const InputBoxSmall = ({
  label,
  defaultValue = "",
  onChange,
  type = "text",
  validators = [],
  name,
  isRequired = false,
  important = false,
  error,
  disablePastDates,
  disableFutureDates,
  className = "",
  border = "",
  readOnly = false,
  isPrice = false,
  maxChar = null,
  restrictionId = null,
}) => {
  const [value, setValue] = useState(defaultValue);
  const [errors, setErrors] = useState([]);
  const dateInputRef = useRef(null);
  const { t } = useTranslation();

  const validatorArray = Array.isArray(validators)
    ? validators
    : validators
      ? [validators]
      : [];

  let effectiveValidators = isRequired
    ? [
        ...(typeof validatorArray === "function"
          ? [validatorArray]
          : validatorArray),
        (val) =>
          (val !== null && val !== undefined && val !== "") ||
          "This field is required",
      ]
    : validatorArray;

  if (restrictionId && RESTRICTIONS[restrictionId]) {
    const config = RESTRICTIONS[restrictionId];
    if (config.minLength) {
      effectiveValidators = [
        ...effectiveValidators,
        (val) =>
          !val ||
          val.length >= config.minLength ||
          config.error ||
          `Minimum ${config.minLength} characters required`,
      ];
    }
  }

  const validateInput = (inputValue) => {
    const newErrors = effectiveValidators
      .map((validator) => {
        if (typeof validator === "function") {
          const result = validator(inputValue);
          return result === true ? null : result;
        }
        return null;
      })
      .filter((error) => typeof error === "string");

    return newErrors;
  };

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleInputChange = (event) => {
    let newValue = event.target.value;

    if (isPrice) {
      const regex = /^(\d*\.?\d{0,2})$/;
      if (!regex.test(newValue)) {
        return;
      }
    }

    if (restrictionId && RESTRICTIONS[restrictionId]) {
      const config = RESTRICTIONS[restrictionId];
      if (config.pattern && !config.pattern.test(newValue)) {
        return;
      }
      if (config.maxLength && newValue.length > config.maxLength) {
        return;
      }
    }

    if (maxChar) {
      newValue = newValue.toUpperCase();
      const alphaNumericOnly = newValue.replace(/[^A-Z0-9]/g, "");
      newValue = alphaNumericOnly.slice(0, maxChar);
    }

    if (type === "small-date" && disableFutureDates && newValue) {
      const today = new Date().toISOString().split("T")[0];
      const inputDate = new Date(newValue);
      const todayDate = new Date(today);

      if (inputDate > todayDate) {
        newValue = "";
        setValue("");
        setErrors([]);

        if (onChange) {
          const modifiedEvent = {
            ...event,
            target: {
              ...event.target,
              value: "",
            },
          };
          onChange(modifiedEvent);
        }
        return;
      }
    }

    const newErrors = validateInput(newValue);

    setValue(newValue);
    setErrors(newErrors);

    if (onChange) {
      const modifiedEvent = {
        ...event,
        target: {
          ...event.target,
          value: newValue,
        },
      };
      onChange(modifiedEvent);
    }
  };

  const handleCalendarIconClick = () => {
    if (dateInputRef.current && !readOnly) {
      dateInputRef.current.showPicker();
    }
  };

  const today = new Date().toISOString().split("T")[0];

  const displayErrors = error ? [error] : errors;

  return (
    <div className={`small-input-box-container posrel ${className}`}>
      <input
        className={`input-box common-text ftw-4 ${
          type === "small-date" ? "input-box-with-icon hide-native-icon" : ""
        } ${displayErrors.length > 0 ? "input-error" : ""} ${border}`}
        type={type === "small-date" ? "date" : type}
        value={value}
        onChange={handleInputChange}
        placeholder=""
        ref={type === "small-date" ? dateInputRef : null}
        name={name}
        readOnly={readOnly}
        min={disablePastDates ? today : undefined}
        max={disableFutureDates ? today : undefined}
      />
      <label
        className={`input-date-label input-label common-text ftw-4 ${
          value ? "has-value" : ""
        } ${type === "small-date" ? "small-date-label" : ""}`}
      >
        {t(label)}
        {important && <span className="required-asterisk">*</span>}
      </label>
      {type === "small-date" && (
        <div
          className="calendar-icon"
          onClick={handleCalendarIconClick}
          style={readOnly ? { pointerEvents: "none", opacity: 0.5 } : {}}
        >
          <svg
            className="calendor-icon-svg"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              opacity="0.5"
              d="M20 8H0V17C0 17.7956 0.316071 18.5587 0.87868 19.1213C1.44129 19.6839 2.20435 20 3 20H17C17.7956 20 18.5587 19.6839 19.1213 19.1213C19.6839 18.5587 20 17.7956 20 17V8ZM5 6C4.73478 6 4.48043 5.89464 4.29289 5.70711C4.10536 5.51957 4 5.26522 4 5V1C4 0.734784 4.10536 0.48043 4.29289 0.292893C4.48043 0.105357 4.73478 0 5 0C5.26522 0 5.51957 0.105357 5.70711 0.292893C5.89464 0.48043 6 0.734784 6 1V5C6 5.26522 5.89464 5.51957 5.70711 5.70711C5.51957 5.89464 5.26522 6 5 6ZM15 6C14.7348 6 14.4804 5.89464 14.2929 5.70711C14.1054 5.51957 14 5.26522 14 5V1C14 0.734784 14.1054 0.48043 14.2929 0.292893C14.4804 0.105357 14.7348 0 15 0C15.2652 0 15.5196 0.105357 15.7071 0.292893C15.8946 0.48043 16 0.734784 16 1V5C16 5.26522 15.8946 5.51957 15.7071 5.70711C15.5196 5.89464 15.2652 6 15 6Z"
              fill="#6D6D6D"
            />
            <path
              d="M17 2H16V5C16 5.26522 15.8946 5.51957 15.7071 5.70711C15.5196 5.89464 15.2652 6 15 6C14.7348 6 14.4804 5.89464 14.2929 5.70711C14.1054 5.51957 14 5.26522 14 5V2H6V5C6 5.26522 5.89464 5.51957 5.70711 5.70711C5.51957 5.89464 5.26522 6 5 6C4.73478 6 4.48043 5.89464 4.29289 5.70711C4.10536 5.51957 4 5.26522 4 5V2H3C2.20435 2 1.44129 2.31607 0.87868 2.87868C0.316071 3.44129 0 4.20435 0 5V8H20V5C20 4.20435 19.6839 3.44129 19.1213 2.87868C18.5587 2.31607 17.7956 2 17 2Z"
              fill="#454545"
            />
          </svg>
        </div>
      )}
    </div>
  );
};

export default InputBoxSmall;
